import { mapOpenWorldNoteTurn } from '@/api/adapters/supabase/open-world-notes'
import { supabase } from '@/lib/supabaseClient'
import { requireOpenWorldSession } from '@/api/adapters/supabase/open-world-auth'
import { OpenWorldPersistenceError } from '@/lib/openWorlds/persistence'
import { isOpenWorldAttemptUuid } from '@/lib/openWorlds/attemptPersistence'
import {
  isOpenWorldJournalJson,
  isOpenWorldJournalRecord,
} from '@/lib/openWorlds/journalPersistence'
import type {
  OpenWorldPreparation,
  SaveOpenWorldPreparationRequest,
} from '@/lib/openWorlds/preparation'

export function mapOpenWorldPreparation(value: unknown): OpenWorldPreparation {
  const fail = (): never => {
    throw new OpenWorldPersistenceError('backend', 'Malformed prepared story context.')
  }
  if (
    !isOpenWorldJournalRecord(value) ||
    !isOpenWorldJournalJson(value) ||
    Object.keys(value)
      .filter((key) => key !== 'noteTurn')
      .sort()
      .join(',') !==
      'attemptId,context,generatedEntries,journalSequence,sourceAttemptId,stateAfter' ||
    !isOpenWorldAttemptUuid(value.attemptId) ||
    (value.sourceAttemptId !== null && !isOpenWorldAttemptUuid(value.sourceAttemptId)) ||
    !Number.isInteger(value.journalSequence) ||
    (value.journalSequence as number) < 0 ||
    !Array.isArray(value.generatedEntries) ||
    !isOpenWorldJournalRecord(value.stateAfter) ||
    !isOpenWorldJournalRecord(value.context)
  )
    fail()
  const context = (value as Record<string, unknown>).context as Record<string, unknown>
  if (
    !Array.isArray(context.system) ||
    !Array.isArray(context.history) ||
    !Array.isArray(context.references) ||
    (context.historicalNotes !== undefined && !Array.isArray(context.historicalNotes)) ||
    typeof context.playerInput !== 'string' ||
    typeof context.task !== 'string' ||
    context.includePlayerInput !== true ||
    !Number.isInteger(context.historyLimit) ||
    (context.historyLimit as number) < 0
  )
    fail()
  if (isOpenWorldJournalRecord(value) && value.noteTurn !== undefined && value.noteTurn !== null)
    mapOpenWorldNoteTurn(value.noteTurn)
  return structuredClone(value) as unknown as OpenWorldPreparation
}

export async function saveOpenWorldPreparation(
  request: SaveOpenWorldPreparationRequest
): Promise<OpenWorldPreparation> {
  if (
    !isOpenWorldAttemptUuid(request.attemptId) ||
    !isOpenWorldAttemptUuid(request.executionToken)
  ) {
    throw new OpenWorldPersistenceError('invalid_input', 'Prepared turn identity must use UUIDs.')
  }
  await requireOpenWorldSession()
  const { data, error } = await supabase.rpc('rpc_save_open_world_preparation_v1', {
    p_attempt_id: request.attemptId,
    p_execution_token: request.executionToken,
    p_context: request.context,
    p_generated_entries: request.generatedEntries,
    p_state_after: request.stateAfter,
  })
  if (error)
    throw new OpenWorldPersistenceError(
      error.message.includes('OW_CONFLICT') ? 'conflict' : 'backend',
      error.message
    )
  return mapOpenWorldPreparation(data)
}

export async function getOpenWorldPreparation(
  attemptId: string
): Promise<OpenWorldPreparation | null> {
  if (!isOpenWorldAttemptUuid(attemptId))
    throw new OpenWorldPersistenceError('invalid_input', 'Prepared turn identity must use UUIDs.')
  await requireOpenWorldSession()
  const { data, error } = await supabase
    .from('open_world_preparations')
    .select('attempt_id,source_attempt_id,context,generated_entries,state_after')
    .eq('attempt_id', attemptId)
    .maybeSingle()
  if (error) throw new OpenWorldPersistenceError('backend', error.message)
  if (data === null) return null
  return mapOpenWorldPreparation({
    attemptId: data.attempt_id,
    sourceAttemptId: data.source_attempt_id,
    context: data.context,
    generatedEntries: data.generated_entries,
    stateAfter: data.state_after,
    journalSequence: 0,
  })
}
